import { ESTADOS, estadoDelHit } from "./estados";
import type { EstadoPublicado } from "./estados";
import { esBorrador } from "./types";
import type { BulkAction, CatalogHit, CatalogRow, DraftRow } from "./types";

/**
 * Qué acciones en lote tiene una fila, según el API (xcons-docs#430), o `null` si el API
 * todavía no las manda. `null` y `[]` no son lo mismo: vacío es "no podés hacer nada con
 * esta fila"; `null` es "no sé", y ahí la pantalla cae a la regla general del nivel.
 */
export const accionesDeLaFila = (row: CatalogRow): BulkAction[] | null =>
  row.bulkActions ?? null;

/**
 * Si la fila tiene checkbox. **Con el contrato de #430 decide el API**, que ya resolvió el
 * nivel propio sobre ESE producto: un Admin por participación marca sus productos aunque su
 * nivel general sea By relation.
 *
 * Sin el contrato queda la regla de antes: Editor for all o más, y un borrador no se marca
 * porque se edita y se manda a revisión.
 */
export const puedeMarcarse = (
  row: CatalogRow,
  puedeEscribirTodo: boolean,
): boolean => {
  const acciones = accionesDeLaFila(row);
  if (acciones) return acciones.length > 0;
  return puedeEscribirTodo && !(esBorrador(row) && row.state === "draft");
};

/**
 * Cuántas filas de la selección admiten una acción. `null` si alguna fila viene sin el
 * contrato de #430: no se puede contar, y la barra se comporta como antes.
 */
export const cuantasAdmiten = (
  seleccion: CatalogRow[],
  accion: BulkAction,
): number | null => {
  let n = 0;
  for (const row of seleccion) {
    const acciones = accionesDeLaFila(row);
    if (!acciones) return null;
    if (acciones.includes(accion)) n++;
  }
  return n;
};

/**
 * Cómo se ofrece Asignar dueño para una selección de publicados:
 *
 * - `oculto`: nadie de la selección lo admite, o el API no manda el contrato. Sin endpoint
 *   que lo respalde, un botón que siempre falla es peor que no tenerlo.
 * - `deshabilitado`: algunos sí y otros no. El criterio de #433 pide el botón visible y
 *   explicado, no un lote que vuelve con la mitad en `PRODUCT_ADMIN_REQUIRED`.
 * - `habilitado`: todos.
 */
export const asignarDuenoPara = (
  seleccion: CatalogRow[],
):
  | { estado: "oculto" }
  | { estado: "deshabilitado"; sinPermiso: number }
  | { estado: "habilitado" } => {
  const pueden = cuantasAdmiten(seleccion, "assignOwner");
  if (!pueden) return { estado: "oculto" };
  if (pueden < seleccion.length) {
    return { estado: "deshabilitado", sinPermiso: seleccion.length - pueden };
  }
  return { estado: "habilitado" };
};

/**
 * Las filas de la selección **que admiten** la acción: son las que se mandan (xcons-docs#453).
 * Mandar el resto sólo las hacía volver como error en el reporte —publicar lo que ya está
 * activo, archivar lo archivado—, y el operador leía "3 con error" por algo que no tenía nada
 * que hacer. Si alguna fila viene sin el contrato de #430, se mandan todas y decide el API.
 */
export const filasQueAdmiten = <R extends CatalogRow>(
  seleccion: R[],
  accion: BulkAction,
): R[] =>
  seleccion.some((row) => !accionesDeLaFila(row))
    ? seleccion
    : seleccion.filter((row) => accionesDeLaFila(row)!.includes(accion));

/** Desde qué estado publicado se llega a cada destino: la matriz del API, para explicar. */
const DESDE: Record<EstadoPublicado, EstadoPublicado[]> = {
  active: ["inactive"],
  inactive: ["active", "archived"],
  archived: ["active", "inactive"],
};

/**
 * Por qué no se toca un producto publicado con esa transición, para el confirm: **ya está
 * así**, **desde ese estado no se puede** (con el camino, si lo hay), o **falta permiso**
 * —la transición existe, pero no es Admin de ese producto—.
 */
export const motivoDeNoTransicion = (
  estado: EstadoPublicado,
  destino: EstadoPublicado,
): string => {
  const plural = `${ESTADOS[estado].label.toLowerCase()}s`;
  if (estado === destino) return `ya están ${plural}`;
  if (estado === "archived" && destino === "active") {
    return "archivados: primero se pasan a Pausado";
  }
  if (DESDE[destino].includes(estado))
    return `${plural}, sin permiso sobre ellos`;
  return `${plural}: desde ahí no se puede`;
};

/**
 * El desglose del confirm de una transición: cuántos se tocan, y de los que no, por qué,
 * agrupados. Es lo que hace honesto al "Publicar 300 productos".
 */
export const desgloseDeTransicion = (
  seleccion: CatalogHit[],
  accion: BulkAction,
  destino: EstadoPublicado,
): {
  seTocan: CatalogHit[];
  noSeTocan: Array<{ motivo: string; n: number }>;
} => {
  const seTocan = filasQueAdmiten(seleccion, accion);
  const tocados = new Set(seTocan);
  const porMotivo = new Map<string, number>();
  for (const row of seleccion) {
    if (tocados.has(row)) continue;
    const motivo = motivoDeNoTransicion(estadoDelHit(row), destino);
    porMotivo.set(motivo, (porMotivo.get(motivo) ?? 0) + 1);
  }
  return {
    seTocan,
    noSeTocan: [...porMotivo.entries()].map(([motivo, n]) => ({ motivo, n })),
  };
};

/**
 * Lo mismo para las versiones sin publicar: aprobar sólo lo que está en revisión, mandar a
 * revisión sólo los borradores. El resto se agrupa por motivo.
 */
export const desgloseDePendientes = (
  seleccion: DraftRow[],
  accion: "approve" | "submit",
): { seTocan: DraftRow[]; noSeTocan: Array<{ motivo: string; n: number }> } => {
  const seTocan = filasQueAdmiten(seleccion, accion);
  const tocados = new Set(seTocan);
  const porMotivo = new Map<string, number>();
  for (const row of seleccion) {
    if (tocados.has(row)) continue;
    const motivo =
      accion === "approve"
        ? row.state === "draft"
          ? "borradores: se mandan a revisión, no se aprueban"
          : "en revisión, sin permiso para aprobarlos"
        : row.state === "review"
        ? "ya están en revisión"
        : "borradores, sin permiso sobre ellos";
    porMotivo.set(motivo, (porMotivo.get(motivo) ?? 0) + 1);
  }
  return {
    seTocan,
    noSeTocan: [...porMotivo.entries()].map(([motivo, n]) => ({ motivo, n })),
  };
};

/**
 * El desglose de **Eliminar borradores** (xcons-docs#473): se eliminan los borradores que el
 * API marcó con `discard`, y el resto queda afuera con su motivo. A diferencia de las otras
 * acciones, vale sobre cualquier selección —publicados incluidos—, porque no hace nada sobre
 * lo que no es un borrador: lo deja afuera y lo dice.
 *
 * Entra también la **versión de edición de un publicado en borrador**, que es en lo que queda
 * un cambio rechazado: eliminarla descarta el cambio, y el producto publicado sigue igual.
 *
 * Si alguna fila viene sin el contrato de #430, se mandan los borradores de alta —lo único que
 * el API viejo elimina— y decide el API, que igual devuelve en el reporte lo que no pudo.
 */
export const desgloseDeEliminacion = (
  seleccion: CatalogRow[],
): { seTocan: DraftRow[]; noSeTocan: Array<{ motivo: string; n: number }> } => {
  const sinContrato = seleccion.some((row) => !accionesDeLaFila(row));
  const seTocan: DraftRow[] = [];
  const porMotivo = new Map<string, number>();
  for (const row of seleccion) {
    const borrador = esBorrador(row) && row.state === "draft";
    const seElimina = sinContrato
      ? borrador && !row.productId
      : borrador && accionesDeLaFila(row)!.includes("discard");
    if (seElimina) {
      seTocan.push(row as DraftRow);
      continue;
    }
    const motivo = !esBorrador(row)
      ? "publicados, pausados o archivados: se archivan, no se eliminan"
      : row.state === "review"
      ? "en revisión: se rechazan, no se eliminan"
      : "borradores sin permiso para eliminarlos";
    porMotivo.set(motivo, (porMotivo.get(motivo) ?? 0) + 1);
  }
  return {
    seTocan,
    noSeTocan: [...porMotivo.entries()].map(([motivo, n]) => ({ motivo, n })),
  };
};

/**
 * Cuánto espera el admin un lote (xcons-docs#557). El API los aplica **de a uno y en
 * serie**, así que el tiempo crece con la cantidad, y no todos pesan igual: aprobar escribe
 * el producto entero en Magento. Con los 30 s generales de axios, aprobar 47 mostraba
 * "No se pudo ejecutar el lote" mientras el API terminaba de aprobarlos todos.
 *
 * Los segundos por producto son holgados a propósito: esperar de más no cuesta nada, cortar
 * antes miente. El tope evita dejar la pantalla colgada si el API no contesta nunca.
 */
const SEGUNDOS_POR_PRODUCTO: Record<string, number> = {
  approve: 5,
  transition: 3,
  submit: 2,
  discard: 1,
  owner: 1,
};
const BASE_MS = 30_000;
const TOPE_MS = 15 * 60_000;

export const tiempoDelLote = (accion: string, cantidad: number): number =>
  Math.min(
    TOPE_MS,
    BASE_MS + cantidad * (SEGUNDOS_POR_PRODUCTO[accion] ?? 3) * 1000,
  );

/**
 * Si el lote se cortó por tiempo y no por un error del API: el timeout del admin, o un 504
 * de algún proxy en el medio. En los dos casos el API puede seguir aplicándolo.
 */
export const seCortoPorTiempo = (e: unknown): boolean => {
  const error = e as { code?: string; response?: { status?: number } };
  return (
    error?.code === "ECONNABORTED" ||
    error?.code === "ETIMEDOUT" ||
    error?.response?.status === 504
  );
};
